import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

interface FAQItem {
  q: string;
  a: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    q: "What platforms does FiverFlow support?",
    a: "Track clients and orders from Fiverr, Upwork, TikTok Shop, Instagram DMs, and private clients — all in one centralized workspace. You can organize everything regardless of where your clients come from."
  },
  {
    q: "How do I manage my clients and orders?",
    a: "Simply add clients and orders as you work. FiverFlow keeps everything organized in one place so you always know what's active, due, and delivered. You can track status, deadlines, budgets, and notes for each project."
  },
  {
    q: "Can I create and send invoices to clients?",
    a: "Yes! With the Scale plan, you can create professional invoices from your orders, customize them with templates, and export them as PDFs to send to your clients. You can track payment status and record payments manually. Note: FiverFlow doesn't process payments directly — you'll handle payment collection through your usual methods."
  },
  {
    q: "What can the AI Assistant do?",
    a: "Jett, our AI assistant (available on Scale plan), helps you draft client replies, summarize orders, answer questions about your business, and speed up administrative tasks. It understands your clients, orders, and workflow to provide relevant assistance so you can focus on delivering great work."
  },
  {
    q: "What features are included in each plan?",
    a: "The free Lunch plan includes up to 5 clients, 10 orders per month, and access to Dashboard, Clients, and Orders pages. Boost adds unlimited clients & orders, Calendar, and Workboard. Scale includes everything plus Statistics, Invoices, and AI Assistant access."
  },
  {
    q: "Is FiverFlow only for solo freelancers?",
    a: "Yes, FiverFlow is designed specifically for solo freelancers who want to organize their business across multiple platforms. Each account is personal and focused on managing your own clients, orders, and workflow."
  },
  {
    q: "Can I change or cancel my plan anytime?",
    a: "Absolutely! You can upgrade, downgrade, or cancel your subscription anytime from your billing settings. Changes take effect immediately, and you'll only be charged for the time you used."
  },
  {
    q: "Do I need a credit card to start?",
    a: "Yes, a credit card is required to create an account. You can start with the free Lunch plan, and upgrade to Boost (includes a 7-day free trial) or Scale whenever you're ready to unlock more features."
  }
];

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-16 md:py-24">
      <div className="max-w-[1300px] mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            FAQ
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {FAQ_ITEMS.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="rounded-xl border border-white/10 bg-[rgba(255,255,255,0.03)] backdrop-blur-xl shadow-[0_30px_120px_rgba(0,0,0,0.8)] overflow-hidden"
            >
              <button
                onClick={() => toggle(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <h3 className="text-lg font-semibold text-white pr-4">
                  {item.q}
                </h3>
                <div className="flex-shrink-0 text-neutral-400">
                  {openIndex === index ? (
                    <Minus size={24} />
                  ) : (
                    <Plus size={24} />
                  )}
                </div>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    id={`faq-answer-${index}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="p-6 pt-0 text-neutral-300">
                      {item.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

